<template>
  <div class="search-div">
    <div @click="handleSearchBarClick" class="input-div">
      <van-search
        v-model="keyword"
        show-action
        :placeholder="key_val"
      >
        <template #left v-if="showBack">
          <div @click.stop="handleBackClick" class="back-icon-div">
            <van-icon size="22" name="arrow-left"  color="#333333" />
          </div>
        </template>
        <template #action>
          <div class="button-div" @click.stop="handleSearchClick">
            <div class="img-btn">
              <img v-webp src="https://image.kilimall.com/kenya/new_wap/userCenter/ic_search@2x.png" alt="" class="searchs-imgs">
            </div>
          </div>
        </template>
      </van-search>
    </div>
  </div>

  <div class="content-wrapper" v-if="!isPreview">
    <div class="associative-words" v-show="suggestions.length > 0">
      <div
        v-for="(item, i) in suggestions"
        :key="i"
        class="word-item"
        @click="handleKeywordClick(item, searchTypeMap.relatedSearch)"
      >
        <p class="label">{{ item }}</p>
        <van-icon name="arrow" color="#333" size="16" />
      </div>
    </div>

    <div class="history-keyword" v-if="historyKeyword.length">
      <p class="history-title">History Search Keys</p>
      <div
        v-for="item in historyKeyword"
        :key="item.id"
        class="history-item"
        @click="handleKeywordClick(item.key, searchTypeMap.historySearch)"
      >
        {{ item.key }}
      </div>
    </div>

    <div class="hot-search-keys" v-if="hotSearchKeys.length">
      <p class="hot-title">Hot Search Keys</p>
      <div class="hot-keys">
        <div
          v-for="(item, idx) in hotSearchKeys"
          :key="idx"
          class="hot-item"
          @click="handleHotKeywordClick(item)"
        >
          <span class="content">
            <span class="index" :index="idx + 1">{{ idx + 1 }}</span>
            <span :style="{ color: item.fontColor, backgroundColor: item.backgroundColor }" class="content-text">{{ item.keyword }}</span>
          </span>
          <span class="remark" :index="idx + 1"><van-icon v-if="idx <= 2" name="fire" />{{ item.remark }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { type ISearchParams, getSuggestions, suggestionClick, getHomeHotKeywordsCache } from '@/api/models/system'
import { getPreviousPageKey } from '@/utils/traffic-source'
import { buildStoreSearchListingSource } from '@/plugins/utils'

interface IProps {
  isPreview?: boolean,
  showBack?: boolean,
  searchBarClick?: Function
  searchFn?: (params: ISearchParams, source: string) => void
}
interface IRouteQuery { keyword?: string, from?: string, first_page?: string }
interface IHistoryKeyword { key: string, id: number }
const HISTORY_KEYWORD_KEY = 'HISTORY_KEYWORD_KEY'
const { $debounce, $hotKeysJump } = useNuxtApp()
const router = useRouter()
const props = withDefaults(
  defineProps<IProps>(),
  {
    isPreview: true,
    showBack: false
  }
)

enum searchTypeMap {
  enterSearch = "enterSearch",
  historySearch = "historySearch",
  relatedSearch = "relatedSearch",
  hotSearch = "hotSearch"
}

let searchType: searchTypeMap = searchTypeMap.enterSearch
const routeQuery = useRoute().query as IRouteQuery

const hotSearchKeys = ref<IHotKeywords[]>([])
if(!props.isPreview) {
  getHomeHotKeywordsCache().then((r: any) => {
    // app原生页 和 品牌页 H5暂不支持
    hotSearchKeys.value = (r.hotKeywords || []).filter((c: IHotKeywords) => ![4, 7].includes(c.linkType))
  })
}

const historyKeyword = ref<IHistoryKeyword[]>((useCookie<IHistoryKeyword[]>(HISTORY_KEYWORD_KEY).value || []))
const keyword = ref<string>(routeQuery.keyword || '')
const suggestions = ref<string[]>([])
watch(
  () => keyword.value,
  $debounce(async function () {
    const res = await getSuggestions(keyword.value)
    suggestions.value = res
  }, 500)
)

const key_val = 'I am searching for...'
const search_fn = (type: 'default' | 'hotKeys' = 'default', data?: any) => {
  // 最多保留15条历史记录
  const historyItem: IHistoryKeyword = { key: keyword.value, id: new Date().getTime() }
  const isRepeatIdx = historyKeyword.value.findIndex((c:IHistoryKeyword) => c.key === keyword.value)

  if(historyItem.key) {
    if(isRepeatIdx >= 0) {
      historyKeyword.value.splice(isRepeatIdx, 1)
    }
    historyKeyword.value.length >= 15 
      ? historyKeyword.value.splice(14, 1, historyItem)
      : historyKeyword.value.unshift(historyItem)
  }
  
  useCookie<IHistoryKeyword[]>(HISTORY_KEYWORD_KEY).value = historyKeyword.value

  // 获取上一个页面（优先 route.query.first_page，其次 BI session 记录）
  const oldRoute = getPreviousPageKey() || 'home'

  let _source: string = `searchProduct|${searchType}|${keyword.value}|${oldRoute}|searchProductResults`
  
  // 店铺搜索：first_page 取自店铺页 URL（cart / productDetail 等）
  if(routeQuery.from === 'store') {
    _source = buildStoreSearchListingSource(keyword.value, useRoute())
  }

  if(type === 'hotKeys') {
    $hotKeysJump(
      { linkType: data.linkType, linkValue: data.linkValue, keyword: keyword.value },
      true,
      _source
    )
  } else {
    props.searchFn && props.searchFn({ keywords: keyword.value }, _source)
  }
}
  
const handleKeywordClick = (kw: string, type: searchTypeMap) => {
  keyword.value = kw
  searchType = type

  search_fn()

  // 联想词统计
  if(type === searchTypeMap.relatedSearch) {
    // 联想词统计
    suggestionClick(kw).catch((e: any) => {
      console.error(`联想词 ${kw} 统计失败：`, e)
    })
  }
}
const handleHotKeywordClick = (item: IHotKeywords) => {
  keyword.value = item.keyword
  searchType = searchTypeMap.hotSearch

  search_fn('hotKeys', item)
}

const handleSearchBarClick = () => {
  if(!props.isPreview) {
    return
  }

  if(props.searchBarClick) {
    props.searchBarClick()
  } else {
    router.push('/search-input')
  }
}


const handleSearchClick = () => {
  if(!props.isPreview) {
    searchType = searchTypeMap.enterSearch

    search_fn()
  }
}

const handleBackClick = () => {
  router.back()
}
</script>
<style lang="scss" scoped>
.search-div {
  background-color: white;
  position: relative;
  width: 100%;
  z-index: 99;
  padding: 12px 20px 0px 20px;
  @include center(); 
  .back-icon-div {
    width: 60px;
  }
  .input-div {
    flex-grow: 1;
    .van-search { padding: 0; background: transparent;}
    :deep() .van-search__content {
      border: 4px solid var(--default_color);
      border-radius: 12px;
      height: 72px;
      padding-left: 28px;
      background-color: #fff;
      .van-search__field{
        height: 100%;
        line-height: 100%;
      }
    }
    :deep() .van-field__control{
      font-size: var(--font_size_h2);
      font-weight: 400;
    }
  }
  .button-div {
    position: absolute;
    right: 20px; 
    top: 12px;
    @include center();
    // width: 94px;
    display: flex;
    height: 72px;
    background-color: #DD3131;
    border: 0.04rem solid #DD3131;
    border-radius: 12px;
    overflow: hidden;
    :deep() .van-button {
        width: 100%;
        height: 100%;
        background-color: var(--default_color);
        border: 1.5px solid var(--default_color);
        border-radius: 10px;
    }
    .img-btn{
      width: 80px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-right: 1px solid #fff;
      &:last-child{
        border: none;
      }

      img {
        width: 40px;
        height: 40px;
      }
    }
  }
  :deep() .van-field__left-icon{
    display: none;
    .van-icon{
        display: none;
    }
  }   
}
:deep() .van-button__text{
    display: flex;
}
.searchs-imgs{
  width: 20px;
  height: 20px;
}

.content-wrapper{
  height: calc(100vh - 100px);
  overflow: auto;
}
.associative-words{
  padding: 30px 10px 0 10px;
  font-size: 28px;
  padding: 0 30px;
  background-color: #fff;
  .word-item{
    height: 80px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid #fff;
    &:last-child{
      border: none;
    }
    .label{
      flex: 1;
    }
  }
}

.history-keyword{
  overflow: hidden;
  background-color: #fff;
  padding: 30px;
  .history-title{
    color: var(--font_color_02);
    font-size: 34px;
    font-weight: 600;
    margin-bottom: 30px;
  }
  .history-item{
    float: left;
    min-width: 80px;
    text-align: center;
    background-color: #efefef;
    padding: 8px 12px;
    border-radius: 4px;
    color: #333333;
    font-size: 28px;
    margin: 0 15px 15px 0;
  }
}

.hot-search-keys{
  background-color: #fff;
  padding: 30px;
  .hot-keys{
    display: flex;
    flex-wrap: wrap;
  }
  .hot-title{
    color: var(--font_color_02);
    font-size: 34px;
    font-weight: 600;
  }
  .hot-item{
    width: 100%;
    font-size: 24px;
    margin-top: 30px;
    display: flex;
    .content{
      flex: 1;
      margin-right: 20px;
      .content-text{
        padding: 4px 10px;
        border-radius: 4px;
      }
    }
  }
  .index{
    display: inline-block;
    background-color: #f4f4f4;
    color: #999;
    width: 40px;
    height: 40px;
    text-align: center;
    line-height: 40px;
    font-weight: 600;
    font-size: 24px;
    border-radius: 4px;
    margin-right: 10px;
  }
  .index[index="1"] {
    background-color: #fde5e5;
    color: #ef0000;
  }
  .index[index="2"] {
    background-color: #fef1e8;
    color: #f87622;
  }
  .index[index="3"] {
    background-color: #fef6e7;
    color: #f87622;
  }
  .remark:deep(.van-icon) {
    margin-right: 6px;
  }
  .remark[index="1"]:deep(.van-icon){
    color: #ef0000;
  }
  .remark[index="2"]:deep(.van-icon){
    color: #f87622;
  }
  .remark[index="3"]:deep(.van-icon){
    color: #f87622;
  }
}
</style>
